import { TextField } from "@mui/material";
import * as React from "react";

import {
  SKILL_NAME_MAX_LENGTH,
  validateSkillName,
} from "@/shared/domain/skills";

import styles from "./SkillHeader.module.css";

interface Props {
  name: string;
  /** False renders the name as plain text, with no click-to-edit affordance. */
  canEdit: boolean;
  /** Persist a new name. Rejects to surface a server-side failure inline. */
  onRename: (next: string) => Promise<void>;
}

/**
 * The skill's name as the page's title, editable in place by clicking it.
 *
 * Matches the dataset page's editable name: read mode is ordinary heading text
 * that reveals a hover affordance, clicking swaps in a field, and blur commits.
 * Escape abandons the edit — without it the only way out of a typo would be to
 * retype the original.
 *
 * Renaming is its own action here, independent of the version editor: what a
 * skill is called and what one of its versions says are separate decisions, and
 * the page no longer makes you enter a form mode to change the former.
 */
export function EditableSkillName({ name, canEdit, onRename }: Props) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(name);
  const [error, setError] = React.useState<string | null>(null);
  // Guards the blur that follows a programmatic close, so Escape doesn't also
  // fire a save on its way out.
  const abandoned = React.useRef(false);

  // Track renames landing from elsewhere (another tab, a mutation refetch)
  // while this isn't the field being typed in.
  React.useEffect(() => {
    if (!editing) {
      setDraft(name);
    }
  }, [name, editing]);

  async function commit() {
    const trimmed = draft.trim();
    if (trimmed === name) {
      setEditing(false);
      setError(null);
      return;
    }
    const invalid = validateSkillName(trimmed);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    try {
      await onRename(trimmed);
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't rename skill.");
    }
  }

  if (!canEdit) {
    return <h1 className={styles.title}>{name}</h1>;
  }

  if (!editing) {
    return (
      <h1 className={styles.title}>
        <button
          type="button"
          className={styles.titleButton}
          onClick={() => {
            setDraft(name);
            setEditing(true);
          }}
          title="Click to rename"
        >
          {name}
        </button>
      </h1>
    );
  }

  return (
    <div className={styles.titleEditor}>
      <TextField
        autoFocus
        size="small"
        value={draft}
        error={error !== null}
        helperText={error ?? undefined}
        inputProps={{
          maxLength: SKILL_NAME_MAX_LENGTH,
          "aria-label": "Skill name",
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          setError(null);
        }}
        onBlur={() => {
          if (abandoned.current) {
            abandoned.current = false;
            return;
          }
          void commit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            abandoned.current = true;
            setDraft(name);
            setError(null);
            setEditing(false);
          }
        }}
      />
    </div>
  );
}
